import { Component, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { INotifiedUser } from '../../interfaces/i-notification';
import { IUser } from '../../interfaces/i-user-profile';

/** The decision made for a single pending-access row. */
type UserDecision = 'approve' | 'deny' | null;

/** Data passed into the PendingAccessUsersDialogComponent. */
export interface IPendingAccessUsersDialogData {
  pendingUsers: INotifiedUser[];
}

/**
 * Dialog that displays users who have requested access to an item.
 * Each row has an Approve and a Deny checkbox (mutually exclusive).
 * On submit, only approved users are returned as {@link IUser} objects
 * so they can be added to the security form's `users` control.
 */
@Component({
  selector: 'app-pending-access-users-dialog',
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatCheckboxModule
  ],
  templateUrl: './pending-access-users-dialog.component.html',
  styleUrl: './pending-access-users-dialog.component.scss'
})
export class PendingAccessUsersDialogComponent {
  readonly #dialogRef = inject<MatDialogRef<PendingAccessUsersDialogComponent, IUser[]>>(MatDialogRef);
  readonly dialogData = inject<IPendingAccessUsersDialogData>(MAT_DIALOG_DATA);

  /** Per-user decision map: userId → 'approve' | 'deny' | null. */
  readonly #decisions = signal<Map<string, UserDecision>>(new Map());

  /**
   * Resolves the display name from a notified user's preferences.
   * Falls back to the top-level userId if no name is present.
   */
  getUserName(user: INotifiedUser): string {
    return (user.preferences['name'] as string | undefined) ?? user.userId;
  }

  /**
   * Resolves the userId from a notified user's preferences,
   * falling back to the top-level userId.
   */
  getUserId(user: INotifiedUser): string {
    return (user.preferences['userId'] as string | undefined) ?? user.userId;
  }

  /** Returns the current decision for a user row. */
  getDecision(userId: string): UserDecision {
    return this.#decisions().get(userId) ?? null;
  }

  /**
   * Handles a checkbox change for a given user and decision type.
   * Selecting one option clears the other (mutually exclusive).
   */
  onDecisionChange(userId: string, decision: UserDecision, checked: boolean): void {
    this.#decisions.update(map => {
      const updated = new Map(map);
      if (checked) {
        updated.set(userId, decision);
      } else if (updated.get(userId) === decision) {
        updated.set(userId, null);
      }
      return updated;
    });
  }

  /** Submits the decisions: only approved users are returned to the caller. */
  confirm(): void {
    const approved = this.dialogData.pendingUsers
      .filter(u => this.getDecision(this.getUserId(u)) === 'approve')
      .map<IUser>(u => ({ userId: this.getUserId(u), name: this.getUserName(u) }));
    this.#dialogRef.close(approved);
  }

  /** Cancels and closes the dialog without returning data. */
  cancel(): void {
    this.#dialogRef.close([]);
  }
}

